// src/pages/Profile.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card, Col, Nav, Row, Spinner, Modal, Form, Button
} from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Profile() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState("info");
    const [activityScore, setActivityScore] = useState(85);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [show2FAModal, setShow2FAModal] = useState(false);

    const [activites, setActivites] = useState([]);
    const [connexions, setConnexions] = useState([]);
    const [loadingActivites, setLoadingActivites] = useState(false);

    const [editForm, setEditForm] = useState({ nom_utilisateur: "", prenom_utilisateur: "", email: "" });
    const [passwordForm, setPasswordForm] = useState({ ancien_mdp: "", nouveau_mdp: "", confirmation: "" });

    const [twoFAEnabled, setTwoFAEnabled] = useState(false);
    const [twoFAPassword, setTwoFAPassword] = useState("");
    const [twoFALoading, setTwoFALoading] = useState(false);

    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => { fetchProfile(); }, []);

    useEffect(() => {
        if (user) {
            setEditForm({
                nom_utilisateur: user.nom_utilisateur || "",
                prenom_utilisateur: user.prenom_utilisateur || "",
                email: user.email || ""
            });
        }
    }, [user]);

    /* ============================================================
       HELPERS
       ============================================================ */
    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") {
                return JSON.parse(userStr);
            }
        } catch (error) {
            console.error("Erreur parsing utilisateur:", error);
            localStorage.removeItem("user");
        }
        return null;
    };

    const getRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffSec = Math.floor((now - date) / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) return "À l'instant";
        if (diffMin < 60) return `Il y a ${diffMin} min`;
        if (diffHour < 24) return `Il y a ${diffHour}h`;
        if (diffDay < 7) return `Il y a ${diffDay}j`;
        return formatDate(dateString);
    };

    const getActivityIcon = (type) => {
        const icons = {
            'facture': 'ri-file-list-line', 'devis': 'ri-file-copy-line',
            'commande': 'ri-shopping-cart-line', 'reglement': 'ri-bank-card-line',
            'stock': 'ri-stack-line', 'inventaire': 'ri-file-list-3-line',
            'bl': 'ri-truck-line', 'connexion': 'ri-login-circle-line'
        };
        return icons[type] || 'ri-history-line';
    };

    const getActivityColor = (type) => {
        const colors = {
            'facture': COLORS.success, 'devis': COLORS.warning,
            'commande': COLORS.info, 'reglement': '#8b5cf6',
            'stock': '#6b7280', 'inventaire': '#ec4899',
            'bl': '#f97316', 'connexion': COLORS.info
        };
        return colors[type] || COLORS.secondary;
    };

    const getActivityLabel = (type) => {
        const labels = {
            'facture': 'Facture', 'devis': 'Devis', 'commande': 'Commande',
            'reglement': 'Règlement', 'stock': 'Stock', 'inventaire': 'Inventaire',
            'bl': 'BL', 'connexion': 'Connexion'
        };
        return labels[type] || type;
    };

    const getRoleBadge = (role) => {
        const configs = {
            admin:        { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Administrateur', icon: 'ri-shield-star-line' },
            gestionnaire: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Gestionnaire',   icon: 'ri-briefcase-line' },
            commercial:   { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', label: 'Commercial',     icon: 'ri-user-star-line' },
            magasinier:   { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Magasinier',     icon: 'ri-box-3-line' }
        };
        const config = configs[role] || { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: role || 'Utilisateur', icon: 'ri-user-line' };
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const getUserInitials = () => {
        if (!user) return '';
        const prenom = user.prenom_utilisateur || user.prenom || '';
        const nom = user.nom_utilisateur || user.nom || '';
        return `${prenom.charAt(0) || ''}${nom.charAt(0) || ''}`.toUpperCase() || 'U';
    };

    const getUserFullName = () => {
        if (!user) return 'Utilisateur';
        const prenom = user.prenom_utilisateur || user.prenom || '';
        const nom = user.nom_utilisateur || user.nom || '';
        return (prenom + ' ' + nom).trim() || 'Utilisateur';
    };

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchActivites = async () => {
        try {
            setLoadingActivites(true);
            const currentUser = getCurrentUser();
            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/activites.php?action=historique&user_id=${userId}&limite=20`,
                { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            const result = await response.json();
            if (result.success) setActivites(result.data);
        } catch (err) {
            console.error("Erreur chargement activités:", err);
        } finally {
            setLoadingActivites(false);
        }
    };

    const fetchConnexions = async () => {
        try {
            const currentUser = getCurrentUser();
            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/activites.php?action=connexions&user_id=${userId}&limite=5`,
                { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            const result = await response.json();
            if (result.success) setConnexions(result.data);
        } catch (err) {
            console.error("Erreur chargement connexions:", err);
        }
    };

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const currentUser = getCurrentUser();

            if (!currentUser) {
                navigate('/pages/signin2');
                return;
            }

            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/utilisateurs.php?action=profil&user_id=${userId}`,
                { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );

            const result = await response.json();

            if (result.success) {
                setUser(result.data);
                setStats(result.data.stats);
                if (result.data.two_factor_enabled !== undefined) {
                    setTwoFAEnabled(result.data.two_factor_enabled == 1);
                }
                if (result.data.stats) {
                    const total = (result.data.stats.factures || 0) + (result.data.stats.devis || 0);
                    setActivityScore(Math.min(100, Math.round((total / 50) * 100)));
                }
                await fetchActivites();
                await fetchConnexions();
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleUpdateProfile = async () => {
        setSaving(true);
        setFormError("");
        try {
            const currentUser = getCurrentUser();
            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/update_profil.php?user_id=${userId}`,
                {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(editForm)
                }
            );

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Profil mis à jour', 'Vos informations ont été enregistrées');
                const updatedUser = { ...currentUser, ...editForm };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                await fetchProfile();
                setShowEditModal(false);
            } else {
                setFormError(result.message);
            }
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (passwordForm.nouveau_mdp !== passwordForm.confirmation) {
            setFormError("Les mots de passe ne correspondent pas");
            return;
        }
        setSaving(true);
        setFormError("");
        try {
            const currentUser = getCurrentUser();
            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/change_password.php?user_id=${userId}`,
                {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ancien_mdp: passwordForm.ancien_mdp,
                        nouveau_mdp: passwordForm.nouveau_mdp
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Mot de passe modifié', 'Votre mot de passe a été changé');
                setPasswordForm({ ancien_mdp: "", nouveau_mdp: "", confirmation: "" });
                setShowPasswordModal(false);
            } else {
                setFormError(result.message);
            }
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggle2FA = async () => {
        setTwoFALoading(true);
        setFormError("");
        try {
            const currentUser = getCurrentUser();
            const userId = currentUser.id_utilisateur || currentUser.id;
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/toggle_2fa.php?user_id=${userId}`,
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ password: twoFAPassword, enabled: !twoFAEnabled })
                }
            );

            const result = await response.json();

            if (result.success) {
                setTwoFAEnabled(!twoFAEnabled);
                await fetchProfile();
                showToast('success',
                    twoFAEnabled ? '2FA désactivée' : '2FA activée',
                    'La sécurité de votre compte a été mise à jour'
                );
                setShow2FAModal(false);
                setTwoFAPassword("");
            } else {
                setFormError(result.message);
            }
        } catch (err) {
            setFormError(err.message);
        } finally {
            setTwoFALoading(false);
        }
    };

    /* ============================================================
       LOADING — Bootstrap spinner (conservé)
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                className="mb-3"
                                style={{ width: '3rem', height: '3rem', color: COLORS.primary }}
                            >
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <h5 className="text-muted">Chargement de votre profil...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="empty-state-charte">
                            <div className="empty-state-icon-charte" style={{ background: 'rgba(239,68,68,0.1)', color: COLORS.danger }}>
                                <i className="ri-error-warning-line"></i>
                            </div>
                            <h6 className="fw-semibold mb-1">Erreur</h6>
                            <p className="text-muted small mb-3">{error}</p>
                            <Button className="btn-modern-outline" onClick={() => navigate('/pages/signin2')}>
                                Retour à la connexion
                            </Button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-user-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Mon profil</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i> {getUserFullName()}
                                    </span>
                                    {user?.email && (
                                        <span className="badge-modern">
                                            <i className="ri-mail-line"></i> {user.email}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-primary" onClick={() => setShowEditModal(true)}>
                                <i className="ri-edit-line"></i> Modifier le profil
                            </Button>
                        </div>
                    </div>

                    <Row className="g-4">

                        {/* ===== COLONNE PRINCIPALE ===== */}
                        <Col xl>

                            {/* CARTE PROFIL */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Body className="p-4">
                                    <div className="d-flex flex-column flex-md-row align-items-center gap-4">
                                        <div className="profile-avatar-large-charte">
                                            {getUserInitials()}
                                        </div>
                                        <div className="flex-grow-1 text-center text-md-start">
                                            <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-start gap-3 mb-2">
                                                <h2 className="fw-bold mb-0" style={{ color: '#1e293b' }}>{getUserFullName()}</h2>
                                                {getRoleBadge(user?.role)}
                                            </div>
                                            <p className="text-muted mb-2">
                                                <i className="ri-mail-line me-2" style={{ color: COLORS.secondary }}></i>
                                                {user?.email}
                                            </p>
                                            <p className="text-muted mb-0">
                                                <i className="ri-calendar-line me-2" style={{ color: COLORS.secondary }}></i>
                                                Membre depuis {formatDate(user?.date_creation)}
                                            </p>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* NAVIGATION PAR ONGLETS */}
                            <Nav variant="pills" className="nav-pills-charte mb-4 flex-wrap">
                                <Nav.Link
                                    className={activeTab === 'info' ? 'active' : ''}
                                    onClick={() => setActiveTab('info')}
                                >
                                    <i className="ri-information-line me-2"></i>Informations
                                </Nav.Link>
                                <Nav.Link
                                    className={activeTab === 'securite' ? 'active' : ''}
                                    onClick={() => setActiveTab('securite')}
                                >
                                    <i className="ri-lock-line me-2"></i>Sécurité
                                </Nav.Link>
                                <Nav.Link
                                    className={activeTab === 'activite' ? 'active' : ''}
                                    onClick={() => setActiveTab('activite')}
                                >
                                    <i className="ri-history-line me-2"></i>Activité
                                </Nav.Link>
                            </Nav>

                            {/* BARRE DE PROGRESSION ACTIVITÉ */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="fw-semibold mb-0" style={{ color: '#1e293b' }}>
                                            <i className="ri-line-chart-line me-2" style={{ color: COLORS.primary }}></i>
                                            Niveau d'activité
                                        </h6>
                                        <span className="fw-bold" style={{ color: COLORS.primary }}>{activityScore}%</span>
                                    </div>
                                    <div className="progress-bar-charte">
                                        <div className="progress-fill-charte"
                                             style={{ width: `${activityScore}%`, background: COLORS.primary }}>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* CONTENU DES ONGLETS */}
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Body className="p-4">

                                    {/* TAB INFO */}
                                    {activeTab === 'info' && (
                                        <div>
                                            <div className="section-title-charte">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                    <i className="ri-user-line"></i>
                                                </span>
                                                <h6>Informations personnelles</h6>
                                            </div>
                                            <Row className="g-3">
                                                <Col md="6">
                                                    <div className="info-card-charte">
                                                        <span className="info-label-charte">Nom</span>
                                                        <p className="info-value-charte">{user?.nom_utilisateur}</p>
                                                    </div>
                                                </Col>
                                                <Col md="6">
                                                    <div className="info-card-charte">
                                                        <span className="info-label-charte">Prénom</span>
                                                        <p className="info-value-charte">{user?.prenom_utilisateur}</p>
                                                    </div>
                                                </Col>
                                                <Col md="12">
                                                    <div className="info-card-charte">
                                                        <span className="info-label-charte">Email</span>
                                                        <p className="info-value-charte">{user?.email}</p>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    {/* TAB SÉCURITÉ */}
                                    {activeTab === 'securite' && (
                                        <div>
                                            <div className="section-title-charte">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                    <i className="ri-shield-line"></i>
                                                </span>
                                                <h6>Sécurité du compte</h6>
                                            </div>

                                            <div className="security-item-charte">
                                                <div>
                                                    <h6 className="fw-semibold mb-1">Mot de passe</h6>
                                                    <p className="text-muted mb-0">••••••••</p>
                                                </div>
                                                <Button variant="link" className="btn-link-charte p-0" onClick={() => setShowPasswordModal(true)}>
                                                    Changer
                                                </Button>
                                            </div>

                                            <div className="security-item-charte">
                                                <div>
                                                    <h6 className="fw-semibold mb-1">Authentification à deux facteurs</h6>
                                                    <span className="status-badge-charte" style={{
                                                        background: twoFAEnabled
                                                            ? 'linear-gradient(135deg, #10b981, #059669)'
                                                            : 'linear-gradient(135deg, #64748b, #475569)'
                                                    }}>
                                                        <i className={twoFAEnabled ? 'ri-check-line' : 'ri-close-line'}></i>
                                                        {twoFAEnabled ? "Activé" : "Désactivé"}
                                                    </span>
                                                </div>
                                                <Button variant="link" className="btn-link-charte p-0" onClick={() => setShow2FAModal(true)}>
                                                    {twoFAEnabled ? "Désactiver" : "Activer"}
                                                </Button>
                                            </div>

                                            <div className="security-item-charte">
                                                <div className="w-100">
                                                    <h6 className="fw-semibold mb-3">Dernières connexions</h6>
                                                    {connexions.slice(0, 3).map((conn, index) => (
                                                        <div key={index} className="connexion-row-charte">
                                                            <div>
                                                                <i className="ri-map-pin-line me-2" style={{ color: COLORS.primary }}></i>
                                                                <span>{conn.ip || 'IP inconnue'}</span>
                                                            </div>
                                                            <small className="text-muted">{getRelativeTime(conn.date_creation)}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB ACTIVITÉ */}
                                    {activeTab === 'activite' && (
                                        <div>
                                            <div className="section-title-charte">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                    <i className="ri-history-line"></i>
                                                </span>
                                                <h6>Historique d'activité</h6>
                                            </div>

                                            {loadingActivites ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                    <p className="mt-3 text-muted small">Chargement des activités...</p>
                                                </div>
                                            ) : activites.length > 0 ? (
                                                <div className="timeline-custom-charte">
                                                    {activites.map((activite, index) => (
                                                        <div key={index} className="timeline-item-charte">
                                                            <div className="d-flex gap-3">
                                                                <div className="activity-icon-charte"
                                                                     style={{
                                                                         background: `${getActivityColor(activite.type)}15`,
                                                                         color: getActivityColor(activite.type)
                                                                     }}>
                                                                    <i className={getActivityIcon(activite.type)}></i>
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                                                                        <div>
                                                                            <p className="fw-semibold mb-1">{activite.description}</p>
                                                                            <span className="category-badge-charte">
                                                                                {getActivityLabel(activite.type)}
                                                                            </span>
                                                                        </div>
                                                                        <small className="text-muted">{getRelativeTime(activite.date_activite)}</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="empty-state-charte">
                                                    <div className="empty-state-icon-charte">
                                                        <i className="ri-history-line"></i>
                                                    </div>
                                                    <h6 className="fw-semibold mb-1">Aucune activité récente</h6>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* ===== COLONNE LATÉRALE ===== */}
                        <Col xl="4" className="d-none d-xl-block">

                            {/* STATISTIQUES */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Body className="p-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-stack-line"></i>
                                        </span>
                                        <h6>Statistiques</h6>
                                    </div>
                                    <div className="d-flex justify-content-around text-center">
                                        <div>
                                            <h3 className="fw-bold mb-1" style={{ color: COLORS.primary }}>{stats?.factures || 0}</h3>
                                            <small className="text-muted">Factures</small>
                                        </div>
                                        <div className="vr"></div>
                                        <div>
                                            <h3 className="fw-bold mb-1" style={{ color: COLORS.primary }}>{stats?.devis || 0}</h3>
                                            <small className="text-muted">Devis</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* CONNEXIONS */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Body className="p-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-login-circle-line"></i>
                                        </span>
                                        <h6>Dernières connexions</h6>
                                    </div>
                                    {connexions.length > 0 ? (
                                        connexions.map((conn, index) => (
                                            <div key={index} className="connexion-block-charte">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <i className="ri-map-pin-line me-2" style={{ color: COLORS.primary }}></i>
                                                        <span className="small">{conn.ip || 'IP inconnue'}</span>
                                                    </div>
                                                    <small className="text-muted">{getRelativeTime(conn.date_creation)}</small>
                                                </div>
                                                {conn.user_agent && (
                                                    <small className="text-muted d-block mt-1">
                                                        <i className="ri-computer-line me-1"></i>
                                                        {conn.user_agent.substring(0, 50)}...
                                                    </small>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted text-center small mb-0">Aucune connexion récente</p>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* CONTACT */}
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Body className="p-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-information-line"></i>
                                        </span>
                                        <h6>Contact</h6>
                                    </div>
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-3">
                                            <i className="ri-mail-line me-2" style={{ color: COLORS.primary }}></i>
                                            <span className="small">{user?.email}</span>
                                        </li>
                                        <li className="mb-3">
                                            <i className="ri-calendar-line me-2" style={{ color: COLORS.primary }}></i>
                                            <span className="small">Membre depuis {formatDate(user?.date_creation)}</span>
                                        </li>
                                        <li>
                                            <i className="ri-time-line me-2" style={{ color: COLORS.primary }}></i>
                                            <span className="small">
                                                Dernière connexion : {user?.derniere_connexion ? formatDate(user.derniere_connexion) : 'N/A'}
                                            </span>
                                        </li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* ===== MODAL MODIFIER PROFIL ===== */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered className="modal-charte">
                    <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-edit-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Modifier le profil</Modal.Title>
                                <p className="text-white-50 mb-0 small">Mettez à jour vos informations</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {formError && (
                            <div className="alert-error-charte mb-3">
                                <div className="d-flex align-items-center">
                                    <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                                    <div className="ms-3">
                                        <strong>Erreur</strong>
                                        <p className="mb-0 small">{formError}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <Form>
                            <Row className="g-3">
                                <Col md="6">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={editForm.nom_utilisateur}
                                            onChange={(e) => setEditForm({...editForm, nom_utilisateur: e.target.value})}
                                            className="form-control-charte"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md="6">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">Prénom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={editForm.prenom_utilisateur}
                                            onChange={(e) => setEditForm({...editForm, prenom_utilisateur: e.target.value})}
                                            className="form-control-charte"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md="12">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={editForm.email}
                                            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                            className="form-control-charte"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowEditModal(false)}>
                            Annuler
                        </Button>
                        <Button className="btn-modern-primary" onClick={handleUpdateProfile} disabled={saving}>
                            {saving ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><i className="ri-save-line me-2"></i>Enregistrer</>}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ===== MODAL CHANGER MOT DE PASSE ===== */}
                <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered className="modal-charte">
                    <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-lock-password-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Changer le mot de passe</Modal.Title>
                                <p className="text-white-50 mb-0 small">Sécurisez votre compte</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {formError && (
                            <div className="alert-error-charte mb-3">
                                <div className="d-flex align-items-center">
                                    <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                                    <div className="ms-3">
                                        <strong>Erreur</strong>
                                        <p className="mb-0 small">{formError}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-label-modern">Ancien mot de passe</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={passwordForm.ancien_mdp}
                                    onChange={(e) => setPasswordForm({...passwordForm, ancien_mdp: e.target.value})}
                                    className="form-control-charte"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-label-modern">Nouveau mot de passe</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={passwordForm.nouveau_mdp}
                                    onChange={(e) => setPasswordForm({...passwordForm, nouveau_mdp: e.target.value})}
                                    className="form-control-charte"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="form-label-modern">Confirmer</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={passwordForm.confirmation}
                                    onChange={(e) => setPasswordForm({...passwordForm, confirmation: e.target.value})}
                                    className="form-control-charte"
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowPasswordModal(false)}>
                            Annuler
                        </Button>
                        <Button className="btn-modern-primary" onClick={handleChangePassword} disabled={saving}>
                            {saving ? <><Spinner size="sm" className="me-2" />Changement...</> : <><i className="ri-check-line me-2"></i>Changer</>}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ===== MODAL 2FA ===== */}
                <Modal show={show2FAModal} onHide={() => {setShow2FAModal(false); setTwoFAPassword(""); setFormError("");}} centered className="modal-charte">
                    <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-shield-keyhole-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">
                                    {twoFAEnabled ? "Désactiver la 2FA" : "Activer la 2FA"}
                                </Modal.Title>
                                <p className="text-white-50 mb-0 small">Authentification à deux facteurs</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {formError && (
                            <div className="alert-error-charte mb-3">
                                <div className="d-flex align-items-center">
                                    <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                                    <div className="ms-3">
                                        <strong>Erreur</strong>
                                        <p className="mb-0 small">{formError}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="text-center py-3">
                            <div className="display-1 mb-4" style={{ color: COLORS.primary }}>
                                <i className="ri-shield-line"></i>
                            </div>
                            <h5 className="mb-3 fw-semibold">
                                {twoFAEnabled ? "Pour désactiver la 2FA" : "Pour activer la 2FA"}
                            </h5>
                            <Form.Group className="mb-4">
                                <Form.Label className="form-label-modern">Confirmez votre mot de passe</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={twoFAPassword}
                                    onChange={(e) => setTwoFAPassword(e.target.value)}
                                    placeholder="Votre mot de passe"
                                    size="lg"
                                    className="form-control-charte"
                                />
                            </Form.Group>
                            <div className="info-banner-charte text-start">
                                <div className="info-banner-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                    <i className="ri-information-line"></i>
                                </div>
                                <div>
                                    <h6 className="fw-semibold mb-1">Sécurité renforcée</h6>
                                    <p className="small text-muted mb-0">
                                        L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => {setShow2FAModal(false); setTwoFAPassword("");}}>
                            Annuler
                        </Button>
                        <Button className="btn-modern-primary" onClick={handleToggle2FA} disabled={twoFALoading || !twoFAPassword}>
                            {twoFALoading ? <><Spinner size="sm" className="me-2" />Chargement...</> : <><i className="ri-shield-check-line me-2"></i>{twoFAEnabled ? "Désactiver" : "Activer"}</>}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== AVATAR ===== */
                .profile-avatar-large-charte {
                    width: 100px;
                    height: 100px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 36px;
                    font-weight: 700;
                    border: 4px solid white;
                    box-shadow: 0 10px 30px rgba(41, 90, 167, 0.3);
                    transition: all 0.3s ease;
                    flex-shrink: 0;
                }
                .profile-avatar-large-charte:hover {
                    transform: scale(1.05);
                }

                /* ===== STATUS BADGE ===== */
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.85rem;
                    border-radius: 50px; font-size: 0.75rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== NAV PILLS ===== */
                .nav-pills-charte {
                    gap: 0.5rem;
                }
                .nav-pills-charte .nav-link {
                    border: 1.5px solid #e2e8f0;
                    background: white;
                    padding: 0.65rem 1.25rem;
                    font-weight: 600;
                    color: #64748b;
                    transition: all 0.25s ease;
                    border-radius: 50px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                }
                .nav-pills-charte .nav-link:hover {
                    color: var(--secondary);
                    background: #f0fbff;
                    border-color: var(--secondary);
                    transform: translateY(-1px);
                }
                .nav-pills-charte .nav-link.active {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white !important;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }

                /* ===== INFO CARD ===== */
                .info-card-charte {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                .info-card-charte:hover {
                    transform: translateY(-2px);
                    border-color: var(--secondary);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }
                .info-label-charte {
                    display: block;
                    color: #64748b;
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    font-weight: 600;
                    margin-bottom: 4px;
                }
                .info-value-charte {
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 1.1rem;
                    margin: 0;
                }

                /* ===== SECURITY ITEM ===== */
                .security-item-charte {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 0.75rem;
                    transition: all 0.2s ease;
                    flex-wrap: wrap;
                }
                .security-item-charte:hover {
                    border-color: var(--secondary);
                }
                .btn-link-charte {
                    color: var(--primary) !important;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .btn-link-charte:hover {
                    color: var(--secondary) !important;
                }
                .connexion-row-charte {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.5rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .connexion-row-charte:last-child {
                    border-bottom: none;
                }

                /* ===== TIMELINE ===== */
                .timeline-custom-charte {
                    position: relative;
                    max-height: 400px;
                    overflow-y: auto;
                    padding-right: 0.5rem;
                }
                .timeline-item-charte {
                    position: relative;
                    padding-bottom: 1.25rem;
                    margin-bottom: 0.5rem;
                    border-left: 2px solid ${COLORS.primary}30;
                    padding-left: 1.5rem;
                }
                .timeline-item-charte:last-child {
                    border-left-color: transparent;
                }
                .timeline-item-charte::before {
                    content: '';
                    position: absolute;
                    left: -6px;
                    top: 0;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: var(--primary);
                    border: 2px solid white;
                }
                .activity-icon-charte {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.1rem;
                    flex-shrink: 0;
                }
                .category-badge-charte {
                    display: inline-flex; align-items: center;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    padding: 0.2rem 0.65rem;
                    border-radius: 50px;
                    font-weight: 600; font-size: 0.7rem;
                }

                /* ===== CONNEXION BLOCK ===== */
                .connexion-block-charte {
                    margin-bottom: 0.75rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .connexion-block-charte:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte {
                    background: #e2e8f0;
                    border-radius: 6px;
                    height: 8px;
                    overflow: hidden;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 6px;
                    transition: width 0.3s ease;
                }

                /* ===== FORM ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .form-control-charte {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.6rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .form-control-charte:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== INFO BANNER ===== */
                .info-banner-charte {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .info-banner-icon-charte {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .profile-avatar-large-charte { width: 80px; height: 80px; font-size: 28px; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
